import './_description.scss';
import React, { useState, useEffect } from 'react';
import { Collapse, Modal, Button } from 'react-bootstrap';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import DescriptionField from './Field';
import ModalPreloader from '../../../../../../components/Modal/Preloader';
import CountDown from '../../../../../../components/CountDown';
import Loader from '../../../../../../components/Loaders/Loader';
import Spinner from '../../../../../../components/Loaders/Spinner';

import { editDescription } from '../../../reducer';
import { servicesGetDescription, servicesUpdateDescription, servicesGetProviderDescription } from '../../../../../../services/endpoints/services';
import { getErrorMessage } from '../../../helpers/getters';
import { delay } from '../../../../../../utils/helper';
import { getCategoriesList } from '../../../../../../redux/actions/services';

const ModalEditDescription = ({ modalData, serviceDispatch, onClose }) => {
  const dispatch = useDispatch();

  const { register, handleSubmit, setValue } = useForm();

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({
    init: false,
    isLoading: true,
    isLoadingProvidesDesc: false,
    success: false,
    error: null,
    open: false,
    descriptions: [],
  });

  const { init, isLoading, isLoadingProvidesDesc, descriptions, open, error, success } = form;
  const mainDescription = descriptions.length ? descriptions[0] : null;
  const otherDescriptions = descriptions.length && descriptions.length > 1 ? descriptions.filter((description, index) => index !== 0) : [];
  const isAutoClose = init && !mainDescription;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getDescription = async () => {
    try {
      const response = await servicesGetDescription(modalData.id);
      await delay(300);
      if (response && response.data) {
        setForm((prevState) => ({
          ...prevState,
          init: true,
          isLoading: false,
          descriptions: response.data,
        }));
      }
    } catch (error) {
      await delay(500);
      const errorMessage = getErrorMessage(error);
      return setForm((prevState) => ({
        ...prevState,
        error: errorMessage,
        init: true,
        isLoading: false,
      }));
    }
  };

  const onOpenOtherLanguages = () => {
    setForm((prevState) => ({
      ...prevState,
      open: !prevState.open,
    }));
  };

  const onSubmit = async (description) => {
    setForm((prevState) => ({
      ...prevState,
      error: null,
      isLoading: true,
    }));

    try {
      const { id } = modal;
      await servicesUpdateDescription(id, description);
      await dispatch(getCategoriesList());
      await setForm((prevState) => ({
        ...prevState,
        success: true,
      }));
      await delay(500);
      return await serviceDispatch(editDescription(id));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      return setForm((prevState) => ({
        ...prevState,
        error: errorMessage,
        isLoading: false,
      }));
    }
  };

  const onCopyDescription = async () => {
    setForm((prevState) => ({
      ...prevState,
      error: null,
      isLoadingProvidesDesc: true,
    }));

    try {
      const response = await servicesGetProviderDescription(modal.id);
      const data = response.data;

      setForm((prevState) => ({
        ...prevState,
        isLoadingProvidesDesc: false,
        descriptions: prevState.descriptions.map((item) => {
          const findTranslation = data?.find((t) => t.language_code === item.language_code);

          if (findTranslation?.value?.trim()) {
            setValue(item.language_code, findTranslation.value);
            return {
              ...item,
              value: findTranslation.value,
            };
          }

          return item;
        }),
      }));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      return setForm((prevState) => ({
        ...prevState,
        error: errorMessage,
        isLoadingProvidesDesc: false,
      }));
    }
  };

  useEffect(() => {
    setModal(modalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (modal === modalData) {
      getDescription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Header closeButton={init}>
        <Modal.Title>
          <span>Edit description</span>
          {' '}
          {modalData && <span>{`(ID: ${modalData.id})`}</span>}
        </Modal.Title>
      </Modal.Header>
      <ModalPreloader isLoading={!init}>
        <Modal.Body>
          {error ? (
            <div
              className={classNames('alert alert-danger', {
                'alert-single': !mainDescription,
              })}
            >
              {error}
            </div>
          ) : null}
          {isLoading ? <Loader success={success} /> : null}
          {mainDescription ? (
            <>
              <div className={`services-description__${mainDescription.direction} services-description__default-translation`}>
                {otherDescriptions.length ? (
                  <div className="services-description__collapse">
                    <span className="collapse-link" onClick={onOpenOtherLanguages}>
                      {open ? 'Hide' : 'Show'}
                      {' '}
                      translations (
                      {otherDescriptions.length}
                      )
                    </span>
                  </div>
                ) : null}
                <DescriptionField
                  register={register}
                  direction={mainDescription.direction}
                  languageName={mainDescription.language_name}
                  languageCode={mainDescription.language_code}
                  value={mainDescription.value}
                />
              </div>
              {otherDescriptions.length ? (
                <Collapse in={open}>
                  <div>
                    {otherDescriptions.map((description, index) => (
                      <DescriptionField
                        key={index}
                        register={register}
                        direction={description.direction}
                        languageName={description.language_name}
                        languageCode={description.language_code}
                        value={description.value}
                      />
                    ))}
                  </div>
                </Collapse>
              ) : null}
            </>
          ) : null}

          {modal?.provider && modal?.provider?.type === 1 ? (
            <Button bsStyle="link" className="p-0" disabled={isLoadingProvidesDesc} onClick={onCopyDescription}>
              {!isLoadingProvidesDesc && <span className="fas fa-copy" />}
              {isLoadingProvidesDesc && <Spinner />}
              <span className="ml-8">Copy from provider</span>
            </Button>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="custom__modal-footer">
          {mainDescription ? (
            <Button type="submit" bsStyle="primary" disabled={isLoading}>
              <span>Save changes</span>
            </Button>
          ) : null}
          <Button disabled={isLoading} onClick={onClose}>
            <span>Close</span>
            {' '}
            {isAutoClose ? (
              <>
                (
                <CountDown seconds={5} onComplete={onClose} />
                )
              </>
            ) : null}
          </Button>
        </Modal.Footer>
      </ModalPreloader>
    </form>
  );
};

ModalEditDescription.propTypes = {
  modalData: PropTypes.any,
  serviceDispatch: PropTypes.func,
  onClose: PropTypes.func,
};

export default ModalEditDescription;
